import { createUnvalidatedValidationSummary, type CoasterProject } from '../schema';
import type {
  CoasterPlatformAdapter,
  FixtureLaunchRequest,
  LaunchResolution,
  SocialWrite,
} from './adapter';
import type {
  AuthenticatedViewer,
  CloudDraftRecord,
  CloudDraftSummary,
  HeroUploadDescriptor,
  HeroAssetBundle,
  ModerationStatus,
  PublicCoasterRecord,
  PublishRequest,
  PublishStatusRecord,
  ReadOnlyShare,
  ReadOnlyShareRecord,
  RemixRequest,
  RemixResult,
  SaveCloudDraftOptions,
  StorageLike,
} from './contracts';
import {
  type PlatformCapabilityId,
  type PlatformCapabilityState,
  type PlatformErrorCode,
  type PlatformResult,
  unavailable,
} from './capabilities';

export const PHASE4_QA_IDENTITIES = {
  'qa.alice': {
    kind: 'authenticated',
    id: 'qa.alice',
    displayName: 'Alice Builder',
    avatarUrl: null,
  },
  'qa.bob': { kind: 'authenticated', id: 'qa.bob', displayName: 'Bob Rider', avatarUrl: null },
} as const satisfies Record<string, AuthenticatedViewer>;

export type Phase4QaIdentityId = keyof typeof PHASE4_QA_IDENTITIES;

interface StoredRevision {
  revisionId: string;
  projectId: string;
  project: CoasterProject;
  status: 'pending_review' | 'public' | 'rejected';
  rejectionReason: string | null;
  createdAt: string;
  publishedAt: string | null;
}

interface StoredProject {
  ownerId: string;
  version: number;
  project: CoasterProject;
  status: ModerationStatus;
  pendingRevisionId: string | null;
  currentPublicRevisionId: string | null;
  publicCoasterId: string | null;
  publicSlug: string | null;
  rejectionReason: string | null;
  updatedAt: string;
}

interface StoredShare {
  shareId: string;
  ownerId: string;
  projectId: string;
  revisionId: string;
  secretHash: string;
  project: CoasterProject;
  createdAt: string;
}

interface Phase4QaState {
  schemaVersion: 1;
  projects: Record<string, StoredProject>;
  revisions: Record<string, StoredRevision>;
  shares: Record<string, StoredShare>;
}

export interface LocalPhase4QaPlatformAdapterOptions {
  storage: StorageLike;
  identity?: Phase4QaIdentityId;
  storageKey?: string;
  now?: () => string;
  idFactory?: (prefix: string) => string;
}

const qaStates = {
  anonymous_rides: {
    id: 'anonymous_rides',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Anonymous rides are available.',
  },
  fixture_launch: {
    id: 'fixture_launch',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Fixture launch IDs are available.',
  },
  canvas_capture: {
    id: 'canvas_capture',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Canvas capture is available.',
  },
  cloud_drafts: {
    id: 'cloud_drafts',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA cloud-draft emulation is available.',
  },
  publishing: {
    id: 'publishing',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA moderation emulation is available.',
  },
  public_catalog: {
    id: 'public_catalog',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA public records are available.',
  },
  read_only_shares: {
    id: 'read_only_shares',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA read-only links are available.',
  },
  remixing: {
    id: 'remixing',
    availability: 'available',
    phaseGate: 'phase_4',
    reason: 'Local QA remixing is available.',
  },
  social_writes: {
    id: 'social_writes',
    availability: 'unavailable',
    phaseGate: 'phase_7',
    reason: 'Boost and favorite writes are not enabled.',
  },
  camera: {
    id: 'camera',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Camera permission is not enabled.',
  },
  microphone: {
    id: 'microphone',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Microphone permission is not enabled.',
  },
  referrals: {
    id: 'referrals',
    availability: 'unavailable',
    phaseGate: 'phase_7',
    reason: 'Referral attribution is not enabled.',
  },
  credits: {
    id: 'credits',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Buncha Credits are not enabled.',
  },
  iap: {
    id: 'iap',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Purchases are not enabled.',
  },
  rewarded_ads: {
    id: 'rewarded_ads',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Rewarded ads are not enabled.',
  },
} as const satisfies Record<PlatformCapabilityId, PlatformCapabilityState>;

function failure<T>(
  code: PlatformErrorCode,
  message: string,
  details?: Readonly<Record<string, unknown>>,
): PlatformResult<T> {
  return {
    ok: false,
    error: { code, message, recoverable: true, retryable: false, ...(details ? { details } : {}) },
  };
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function emptyState(): Phase4QaState {
  return { schemaVersion: 1, projects: {}, revisions: {}, shares: {} };
}

function slugify(title: string, suffix: string): string {
  const stem =
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'coaster';
  return `${stem}-${suffix
    .replace(/[^a-z0-9]/gi, '')
    .slice(-8)
    .toLowerCase()}`;
}

async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export class LocalPhase4QaPlatformAdapter implements CoasterPlatformAdapter {
  readonly capabilities = qaStates;
  private identity: Phase4QaIdentityId;
  private readonly storage: StorageLike;
  private readonly storageKey: string;
  private readonly now: () => string;
  private readonly idFactory: (prefix: string) => string;

  constructor(options: LocalPhase4QaPlatformAdapterOptions) {
    this.storage = options.storage;
    this.identity = options.identity ?? 'qa.alice';
    this.storageKey = options.storageKey ?? 'buncha-coasters.phase4-qa-platform.v1';
    this.now = options.now ?? (() => new Date().toISOString());
    this.idFactory = options.idFactory ?? ((prefix) => `${prefix}.${crypto.randomUUID()}`);
  }

  setFixtureIdentity(identity: Phase4QaIdentityId): void {
    this.identity = identity;
  }

  getCapability(id: PlatformCapabilityId): PlatformCapabilityState {
    return this.capabilities[id];
  }

  async getViewer(): Promise<PlatformResult<AuthenticatedViewer>> {
    return { ok: true, value: clone(PHASE4_QA_IDENTITIES[this.identity]) };
  }

  async resolveFixtureLaunch(
    request: FixtureLaunchRequest,
  ): Promise<PlatformResult<LaunchResolution>> {
    if (!request.coasterId) return { ok: true, value: { kind: 'blank' } };
    if (request.coasterId.startsWith('fixture.'))
      return { ok: true, value: { kind: 'fixture', fixtureId: request.coasterId } };
    return failure('not_found', 'The requested fixture does not exist.');
  }

  async listCloudDrafts(): Promise<PlatformResult<readonly CloudDraftSummary[]>> {
    const state = this.read();
    const summaries = Object.values(state.projects)
      .filter((record) => record.ownerId === this.identity)
      .map((record) => ({
        projectId: record.project.projectId,
        title: record.project.metadata.title,
        version: record.version,
        status: record.status,
        publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
        publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
        updatedAt: record.updatedAt,
      }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return { ok: true, value: summaries };
  }

  async loadCloudDraft(projectId: string): Promise<PlatformResult<CloudDraftRecord>> {
    const record = this.read().projects[projectId];
    if (!record) return failure('not_found', 'Draft not found.');
    if (record.ownerId !== this.identity) return failure('not_found', 'Draft not found.');
    return { ok: true, value: this.cloudRecord(record) };
  }

  async saveCloudDraft(
    project: CoasterProject,
    options: SaveCloudDraftOptions = {},
  ): Promise<PlatformResult<CloudDraftRecord>> {
    const state = this.read();
    const existing = state.projects[project.projectId];
    if (existing && existing.ownerId !== this.identity)
      return failure('forbidden', 'Only the owner can update this draft.');
    if (existing && options.expectedVersion !== existing.version)
      return failure('conflict', 'This draft changed in another session.', {
        currentVersion: existing.version,
      });
    if (!existing && options.expectedVersion !== undefined && options.expectedVersion !== null)
      return failure('conflict', 'This draft does not exist at the expected version.', {
        currentVersion: null,
      });

    const viewer = PHASE4_QA_IDENTITIES[this.identity];
    const saved = clone(project);
    saved.metadata.creator = { kind: 'buncha', id: viewer.id, displayName: viewer.displayName };
    saved.metadata.updatedAt = this.now();
    const record: StoredProject = {
      ownerId: this.identity,
      version: (existing?.version ?? 0) + 1,
      project: saved,
      status: existing?.status === 'pending_review' ? 'pending_review' : 'draft_private',
      pendingRevisionId: existing?.pendingRevisionId ?? null,
      currentPublicRevisionId: existing?.currentPublicRevisionId ?? null,
      publicCoasterId: existing?.publicCoasterId ?? null,
      publicSlug: existing?.publicSlug ?? null,
      rejectionReason: existing?.rejectionReason ?? null,
      updatedAt: saved.metadata.updatedAt,
    };
    state.projects[project.projectId] = record;
    this.write(state);
    return { ok: true, value: this.cloudRecord(record) };
  }

  async deleteCloudDraft(
    projectId: string,
    expectedVersion: number,
  ): Promise<PlatformResult<null>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    if (record.version !== expectedVersion)
      return failure('conflict', 'This draft changed in another session.', {
        currentVersion: record.version,
      });
    delete state.projects[projectId];
    this.write(state);
    return { ok: true, value: null };
  }

  async requestPublish(request: PublishRequest): Promise<PlatformResult<PublishStatusRecord>> {
    const state = this.read();
    const record = state.projects[request.projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    if (record.version !== request.expectedVersion)
      return failure('conflict', 'Save the latest draft before publishing.', {
        currentVersion: record.version,
      });
    if (record.pendingRevisionId)
      return failure('conflict', 'This coaster already has a revision in review.');
    const revisionId = this.idFactory('published-revision');
    state.revisions[revisionId] = {
      revisionId,
      projectId: request.projectId,
      project: clone(record.project),
      status: 'pending_review',
      rejectionReason: null,
      createdAt: this.now(),
      publishedAt: null,
    };
    record.pendingRevisionId = revisionId;
    record.status = 'pending_review';
    record.rejectionReason = null;
    record.updatedAt = this.now();
    this.write(state);
    return { ok: true, value: this.publishStatus(record) };
  }

  async moderatePending(
    projectId: string,
    decision: { kind: 'approve' } | { kind: 'reject'; reason: string },
  ): Promise<PlatformResult<PublishStatusRecord>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record?.pendingRevisionId) return failure('not_found', 'No pending revision exists.');
    const revision = state.revisions[record.pendingRevisionId];
    if (!revision) return failure('platform_error', 'The pending revision is missing.');
    if (decision.kind === 'approve') {
      revision.status = 'public';
      revision.publishedAt = this.now();
      record.currentPublicRevisionId = revision.revisionId;
      record.publicCoasterId ??= this.idFactory('public-coaster');
      record.publicSlug ??= slugify(revision.project.metadata.title, record.publicCoasterId);
      record.status = 'public';
      record.rejectionReason = null;
    } else {
      revision.status = 'rejected';
      revision.rejectionReason = decision.reason;
      record.status = 'rejected';
      record.rejectionReason = decision.reason;
    }
    record.pendingRevisionId = null;
    record.updatedAt = this.now();
    this.write(state);
    return { ok: true, value: this.publishStatus(record) };
  }

  async getPublishStatus(projectId: string): Promise<PlatformResult<PublishStatusRecord>> {
    const record = this.read().projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    return { ok: true, value: this.publishStatus(record) };
  }

  async requestHeroUpload(projectId: string): Promise<PlatformResult<HeroUploadDescriptor>> {
    const record = this.read().projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    const expiresAt = new Date(Date.parse(this.now()) + 10 * 60_000).toISOString();
    const asset = <W extends number, H extends number>(role: string, width: W, height: H) => ({
      uploadId: this.idFactory(`hero-${role}`),
      objectKey: `qa/coasters/${projectId}/${role}`,
      uploadUrl: `local-qa://hero/${projectId}/${role}`,
      acceptedContentTypes: ['image/webp', 'image/png'] as const,
      maxBytes: 2_000_000,
      requiredWidth: width,
      requiredHeight: height,
    });
    return {
      ok: true,
      value: {
        expiresAt,
        assets: {
          hero16x9: asset('hero16x9', 1280, 720),
          openGraph: asset('openGraph', 1200, 630),
        },
      },
    };
  }

  async uploadHeroAssets(
    projectId: string,
    assets: HeroAssetBundle,
  ): Promise<PlatformResult<HeroUploadDescriptor>> {
    const descriptor = await this.requestHeroUpload(projectId);
    if (!descriptor.ok) return descriptor;
    const pairs = [
      [descriptor.value.assets.hero16x9, assets.hero16x9],
      [descriptor.value.assets.openGraph, assets.openGraph],
    ] as const;
    for (const [target, blob] of pairs) {
      if (!target.acceptedContentTypes.includes(blob.type as 'image/webp' | 'image/png'))
        return failure('validation_failed', 'Hero images must be WebP or PNG files.');
      if (blob.size > target.maxBytes)
        return failure('validation_failed', 'A hero image exceeds the upload size limit.');
    }
    return descriptor;
  }

  async getPublicCoaster(reference: {
    publicCoasterId?: string;
    publicSlug?: string;
  }): Promise<PlatformResult<PublicCoasterRecord>> {
    const state = this.read();
    const record = Object.values(state.projects).find(
      (candidate) =>
        (reference.publicCoasterId && candidate.publicCoasterId === reference.publicCoasterId) ||
        (reference.publicSlug && candidate.publicSlug === reference.publicSlug),
    );
    if (!record?.currentPublicRevisionId || !record.publicCoasterId || !record.publicSlug)
      return failure('not_found', 'Public coaster not found.');
    const revision = state.revisions[record.currentPublicRevisionId];
    if (!revision || revision.status !== 'public')
      return failure('not_found', 'Public coaster not found.');
    return { ok: true, value: this.publicRecord(record, revision) };
  }

  async createReadOnlyShare(projectId: string): Promise<PlatformResult<ReadOnlyShare>> {
    const state = this.read();
    const record = state.projects[projectId];
    if (!record || record.ownerId !== this.identity)
      return failure('not_found', 'Draft not found.');
    if (!record.project.permissions.allowReadOnlyShare)
      return failure('forbidden', 'Enable read-only sharing for this coaster first.');
    const shareId = this.idFactory('share');
    const secret = this.idFactory('capability');
    const createdAt = this.now();
    state.shares[shareId] = {
      shareId,
      ownerId: this.identity,
      projectId,
      revisionId: record.project.revisionId,
      secretHash: await sha256(secret),
      project: clone(record.project),
      createdAt,
    };
    this.write(state);
    return {
      ok: true,
      value: {
        shareId,
        secret,
        path: `/coasters/share/${shareId}#cap=${encodeURIComponent(secret)}`,
        projectId,
        revisionId: record.project.revisionId,
        createdAt,
      },
    };
  }

  async revokeReadOnlyShare(shareId: string): Promise<PlatformResult<null>> {
    const state = this.read();
    const share = state.shares[shareId];
    if (!share || share.ownerId !== this.identity)
      return failure('not_found', 'Share link not found.');
    delete state.shares[shareId];
    this.write(state);
    return { ok: true, value: null };
  }

  async loadReadOnlyShare(
    shareId: string,
    secret: string,
  ): Promise<PlatformResult<ReadOnlyShareRecord>> {
    const share = this.read().shares[shareId];
    if (!share || share.secretHash !== (await sha256(secret)))
      return failure('not_found', 'Share link not found.');
    return {
      ok: true,
      value: {
        shareId,
        projectId: share.projectId,
        revisionId: share.revisionId,
        title: share.project.metadata.title,
        project: clone(share.project),
        allowRemix: share.project.permissions.allowRemix,
      },
    };
  }

  async remixCoaster(request: RemixRequest): Promise<PlatformResult<RemixResult>> {
    let source: CoasterProject | null = null;
    let sourceRevisionId = '';
    if (request.publicCoasterId) {
      const publicResult = await this.getPublicCoaster({
        publicCoasterId: request.publicCoasterId,
      });
      if (!publicResult.ok) return publicResult;
      source = publicResult.value.project;
      sourceRevisionId = publicResult.value.revisionId;
    } else if (request.share) {
      const shareResult = await this.loadReadOnlyShare(request.share.shareId, request.share.secret);
      if (!shareResult.ok) return shareResult;
      source = shareResult.value.project;
      sourceRevisionId = shareResult.value.revisionId;
    }
    if (!source)
      return failure('invalid_request', 'Choose a public coaster or read-only share to remix.');
    if (!source.permissions.allowRemix)
      return failure('forbidden', 'The creator has not enabled remixing.');

    const now = this.now();
    const sourceProjectId = source.projectId;
    const remix = clone(source);
    remix.projectId = this.idFactory('project');
    remix.revisionId = this.idFactory('revision');
    remix.metadata.title = `${source.metadata.title} Remix`.slice(0, 80);
    remix.metadata.createdAt = now;
    remix.metadata.updatedAt = now;
    const viewer = PHASE4_QA_IDENTITIES[this.identity];
    remix.metadata.creator = { kind: 'buncha', id: viewer.id, displayName: viewer.displayName };
    remix.permissions.visibility = 'private';
    remix.permissions.allowReadOnlyShare = false;
    remix.validation = createUnvalidatedValidationSummary();
    remix.lineage = {
      sourceProjectId,
      sourceRevisionId,
      rootProjectId: source.lineage?.rootProjectId ?? sourceProjectId,
      remixedAt: now,
    };
    const saved = await this.saveCloudDraft(remix);
    if (!saved.ok) return saved;
    return { ok: true, value: { draft: saved.value, lineage: clone(remix.lineage) } };
  }

  async publish(projectId: string, _revisionId: string): Promise<PlatformResult<never>> {
    const record = this.read().projects[projectId];
    if (!record) return failure('not_found', 'Draft not found.');
    const result = await this.requestPublish({ projectId, expectedVersion: record.version });
    return result.ok ? { ok: true, value: undefined as never } : result;
  }

  async writeSocial(_action: SocialWrite): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.social_writes);
  }
  async requestCamera(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.camera);
  }
  async requestMicrophone(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.microphone);
  }
  async attributeReferral(_token: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.referrals);
  }
  async getCreditBalance(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.credits);
  }
  async purchase(_sku: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.iap);
  }
  async showRewardedAd(_placement: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.rewarded_ads);
  }

  private read(): Phase4QaState {
    const raw = this.storage.getItem(this.storageKey);
    if (!raw) return emptyState();
    try {
      const parsed = JSON.parse(raw) as Phase4QaState;
      return parsed.schemaVersion === 1 ? parsed : emptyState();
    } catch {
      return emptyState();
    }
  }

  private write(state: Phase4QaState): void {
    this.storage.setItem(this.storageKey, JSON.stringify(state));
  }

  private cloudRecord(record: StoredProject): CloudDraftRecord {
    return {
      projectId: record.project.projectId,
      ownerId: record.ownerId,
      version: record.version,
      project: clone(record.project),
      status: record.status,
      currentPublicRevisionId: record.currentPublicRevisionId,
      publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
      publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
      updatedAt: record.updatedAt,
    };
  }

  private publishStatus(record: StoredProject): PublishStatusRecord {
    return {
      projectId: record.project.projectId,
      status: record.status,
      pendingRevisionId: record.pendingRevisionId,
      currentPublicRevisionId: record.currentPublicRevisionId,
      publicCoasterId: record.currentPublicRevisionId ? record.publicCoasterId : null,
      publicSlug: record.currentPublicRevisionId ? record.publicSlug : null,
      rejectionReason: record.rejectionReason,
      updatedAt: record.updatedAt,
    };
  }

  private publicRecord(record: StoredProject, revision: StoredRevision): PublicCoasterRecord {
    const project = clone(revision.project);
    return {
      publicCoasterId: record.publicCoasterId!,
      publicSlug: record.publicSlug!,
      projectId: record.project.projectId,
      revisionId: revision.revisionId,
      title: project.metadata.title,
      description: project.metadata.description,
      creator: { id: record.ownerId, displayName: project.metadata.creator.displayName },
      themeId: project.appearance.environmentThemeId,
      heroUrl: null,
      allowRemix: project.permissions.allowRemix,
      publishedAt: revision.publishedAt!,
      project,
    };
  }
}

export function createLocalPhase4QaPlatformAdapter(
  options: LocalPhase4QaPlatformAdapterOptions,
): LocalPhase4QaPlatformAdapter {
  return new LocalPhase4QaPlatformAdapter(options);
}
